/**
 * W59 (2026-05-17) — Position-Health-Card: liest v5_position_health (vom
 * taeglichen Backend-Cron 'refresh-position-health' 21:00 UTC befuellt).
 *
 * Zeigt pro offene Position:
 *  - Health-Score 0-100 + Tier-Badge (strong/ok/caution/exit_signal)
 *  - Stop-Empfehlung (hold/move_to_be/trail_ma50/lock_in_partial/exit_now)
 *  - 1-Liner Headline + Reasoning + Warnings
 *  - Datapoints: R-Multiple, %-vs-Entry, days held, Score-Trend, Earnings-Days
 *
 * UX: Card oben im Dashboard, sortiert nach Health-Score (lowest first =
 * "needs attention").
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Heart,
  RefreshCw,
  ShieldAlert,
  TrendingUp,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { Sparkles } from 'lucide-react';
import { CARD, CARD_ACTION, CARD_HEADER, EMPTY_STATE } from '@/lib/design-tokens';

// W60 — Earnings-Hold-Decision Response
interface EarningsHoldResponse {
  ok: boolean;
  symbol?: string;
  decision?: 'hold' | 'exit_before' | 'reduce_size';
  confidence?: number;
  headline?: string;
  reasoning?: string;
  pros_holding?: string[];
  cons_holding?: string[];
  days_to_earnings?: number;
  error?: string;
}

interface HealthRow {
  position_id: string;
  symbol: string;
  health_score: number;
  health_tier: 'strong' | 'ok' | 'caution' | 'exit_signal';
  stop_action:
    | 'hold'
    | 'move_to_be'
    | 'trail_ma50'
    | 'trail_atr'
    | 'lock_in_partial'
    | 'exit_now';
  stop_recommendation_price: number | null;
  headline: string;
  reasoning: string | null;
  warnings: string[] | null;
  pct_above_entry: number | null;
  current_r_multiple: number | null;
  days_held: number | null;
  /** W100 — Zeit-Stopp der Position; null = kein Time-Stop (longterm/Tranchen). */
  max_hold_days?: number | null;
  v51_score_now: number | null;
  v51_score_delta_7d: number | null;
  ext_above_ma50: number | null;
  ext_above_ma200: number | null;
  next_earnings_days: number | null;
  computed_at: string;
}

const TIER_STYLE: Record<HealthRow['health_tier'], { cls: string; label: string; icon: typeof CheckCircle2 }> = {
  strong: {
    cls: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200',
    label: 'STRONG',
    icon: CheckCircle2,
  },
  ok: {
    cls: 'bg-zinc-600/20 border-zinc-600/40 text-zinc-200',
    label: 'OK',
    icon: Heart,
  },
  caution: {
    cls: 'bg-amber-500/15 border-amber-500/40 text-amber-200',
    label: 'CAUTION',
    icon: AlertTriangle,
  },
  exit_signal: {
    cls: 'bg-red-500/20 border-red-500/50 text-red-200',
    label: 'EXIT-SIGNAL',
    icon: ShieldAlert,
  },
};

const STOP_ACTION_LABEL: Record<HealthRow['stop_action'], string> = {
  hold: 'Halten',
  move_to_be: 'Stop auf Breakeven',
  trail_ma50: 'Stop unter MA50 trailen',
  trail_atr: 'ATR-Stop verengen',
  lock_in_partial: '1/3 Gewinn mitnehmen',
  exit_now: 'Sofort raus',
};

const STOP_ACTION_CLS: Record<HealthRow['stop_action'], string> = {
  hold: 'bg-zinc-700/30 text-zinc-300',
  move_to_be: 'bg-blue-500/20 text-blue-200',
  trail_ma50: 'bg-cyan-500/20 text-cyan-200',
  trail_atr: 'bg-cyan-500/20 text-cyan-200',
  lock_in_partial: 'bg-emerald-500/20 text-emerald-200',
  exit_now: 'bg-red-500/30 text-red-200',
};

export function PositionHealthCard() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<HealthRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // W60 — Earnings-Hold-Decision per Position
  const [earningsDecisions, setEarningsDecisions] = useState<
    Map<string, EarningsHoldResponse | { loading: true }>
  >(new Map());

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const [{ data }, { data: openPos }] = await Promise.all([
        supabase
          .from('v5_position_health')
          .select('*')
          .order('health_score', { ascending: true })
          .limit(20),
        // Audit 15.06. — offene Positionen, um stale Health-Zeilen geschlossener
        // Positionen rauszufiltern (Schließen räumt v5_position_health nicht mit).
        supabase
          .from('v5_positions')
          .select('id')
          .in('status', ['open', 'partially_closed']),
      ]);
      const openIds = new Set(((openPos ?? []) as Array<{ id: string }>).map((p) => p.id));
      setRows(((data ?? []) as HealthRow[]).filter((h) => openIds.has(h.position_id)));
    } finally {
      setLoading(false);
    }
  }

  async function forceRefresh() {
    setRefreshing(true);
    try {
      const res = await fetch('/api/admin/refresh-position-health', { method: 'POST' });
      if (res.ok) await load();
    } catch {
      // ignore
    } finally {
      setRefreshing(false);
    }
  }

  // W60 — Earnings-Hold-Decision on-demand
  async function fetchEarningsDecision(symbol: string, ev?: React.MouseEvent) {
    ev?.stopPropagation();
    setEarningsDecisions((prev) => {
      const next = new Map(prev);
      next.set(symbol, { loading: true });
      return next;
    });
    try {
      const res = await fetch(
        `/api/admin/explain-earnings-hold?symbol=${encodeURIComponent(symbol)}`,
        { method: 'POST' }
      );
      const json = (await res.json()) as EarningsHoldResponse;
      setEarningsDecisions((prev) => {
        const next = new Map(prev);
        next.set(symbol, json);
        return next;
      });
    } catch {
      setEarningsDecisions((prev) => {
        const next = new Map(prev);
        next.set(symbol, { ok: false, error: 'fetch_failed' });
        return next;
      });
    }
  }

  if (loading) {
    return (
      <div className={CARD}>
        <div className="text-xs text-zinc-500">Lade Position-Health...</div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className={CARD}>
        <h3 className={cn(CARD_HEADER, 'mb-2')}>
          <Heart className="size-4 text-zinc-500" />
          Position-Health
        </h3>
        <p className={EMPTY_STATE}>
          Keine offenen Positionen oder Health-Cron noch nicht gelaufen. Der Cron
          laeuft taeglich 21:00 UTC (1h nach US-Close). Erste Berechnung manuell
          triggern?
        </p>
        <button
          type="button"
          onClick={() => void forceRefresh()}
          disabled={refreshing}
          className="mt-2 inline-flex items-center gap-1 text-[11px] text-zinc-300 hover:text-zinc-100 border border-zinc-700 hover:border-zinc-500 px-2 py-1 rounded transition-colors"
        >
          <RefreshCw className={cn('size-3', refreshing && 'animate-spin')} />
          {refreshing ? 'Berechne...' : 'Health jetzt berechnen'}
        </button>
      </div>
    );
  }

  // Hat irgendeine Position Exit-Signal?
  const exitCount = rows.filter((r) => r.health_tier === 'exit_signal').length;
  const cautionCount = rows.filter((r) => r.health_tier === 'caution').length;

  return (
    <div className={cn(CARD_ACTION, 'space-y-3')}>
      <div className="flex items-center justify-between">
        <h3 className={CARD_HEADER}>
          <Heart className="size-4 text-pink-400" />
          Position-Health
          {exitCount > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 border border-red-500/40 text-red-200 font-mono">
              {exitCount} EXIT
            </span>
          )}
          {cautionCount > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/40 text-amber-200 font-mono">
              {cautionCount} CAUTION
            </span>
          )}
        </h3>
        <button
          type="button"
          onClick={() => void forceRefresh()}
          disabled={refreshing}
          className="text-[10px] text-zinc-500 hover:text-zinc-300 inline-flex items-center gap-1"
          title="Neu berechnen"
        >
          <RefreshCw className={cn('size-3', refreshing && 'animate-spin')} />
        </button>
      </div>

      <ul className="space-y-2">
        {rows.map((r) => {
          const tier = TIER_STYLE[r.health_tier];
          const TierIcon = tier.icon;
          return (
            <li
              key={r.position_id}
              onClick={() => navigate(`/symbol/swing/${encodeURIComponent(r.symbol)}`)}
              className={cn(
                'rounded-md border p-2.5 cursor-pointer hover:bg-zinc-900/40 transition-colors',
                tier.cls
              )}
            >
              {/* Header-Zeile */}
              <div className="flex items-center gap-2 mb-1.5">
                <TierIcon className="size-3.5 shrink-0" />
                <span className="font-mono font-semibold text-zinc-100 text-sm">
                  {r.symbol}
                </span>
                <span className="text-[10px] font-mono opacity-70">
                  {tier.label} {r.health_score}/100
                </span>
                <span
                  className={cn(
                    'ml-auto text-[10px] px-2 py-0.5 rounded font-mono font-semibold',
                    STOP_ACTION_CLS[r.stop_action]
                  )}
                >
                  → {STOP_ACTION_LABEL[r.stop_action]}
                  {r.stop_recommendation_price !== null && (
                    <span className="ml-1 opacity-80">
                      @ {r.stop_recommendation_price.toFixed(2)}
                    </span>
                  )}
                </span>
                <ChevronRight className="size-3 opacity-50" />
              </div>

              {/* Reasoning */}
              <p className="text-[11px] text-zinc-300 leading-snug mb-1.5">
                {r.reasoning}
              </p>

              {/* Warnings */}
              {r.warnings && r.warnings.length > 0 && (
                <ul className="space-y-0.5 mb-1.5">
                  {r.warnings.map((w, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-1 text-[10px] text-amber-300"
                    >
                      <AlertCircle className="size-3 shrink-0 mt-0.5" />
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              )}

              {/* W60 — Earnings-Hold-Decision Button + Result */}
              {r.next_earnings_days !== null && r.next_earnings_days >= 0 && r.next_earnings_days <= 21 && (
                (() => {
                  const decision = earningsDecisions.get(r.symbol);
                  if (!decision) {
                    return (
                      <button
                        type="button"
                        onClick={(ev) => void fetchEarningsDecision(r.symbol, ev)}
                        className="mt-1 inline-flex items-center gap-1 text-[10px] text-purple-300 hover:text-purple-200 border border-purple-500/40 hover:border-purple-500/60 bg-purple-500/10 hover:bg-purple-500/20 px-2 py-1 rounded transition-colors"
                      >
                        <Sparkles className="size-3" />
                        Earnings-Entscheidung anfragen (in {r.next_earnings_days}T)
                      </button>
                    );
                  }
                  if ('loading' in decision) {
                    return (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-purple-300">
                        <Sparkles className="size-3 animate-pulse" />
                        KI denkt nach...
                      </div>
                    );
                  }
                  if (!decision.ok) {
                    return (
                      <div className="mt-1 text-[10px] text-red-300">
                        Fehler: {decision.error}
                      </div>
                    );
                  }
                  return (
                    <div
                      onClick={(ev) => ev.stopPropagation()}
                      className={cn(
                        'mt-2 rounded-md border p-2 text-[11px]',
                        decision.decision === 'exit_before'
                          ? 'border-red-500/40 bg-red-500/10'
                          : decision.decision === 'reduce_size'
                            ? 'border-amber-500/40 bg-amber-500/10'
                            : 'border-emerald-500/40 bg-emerald-500/10'
                      )}
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        <Sparkles className="size-3 text-purple-300" />
                        <span className="font-semibold text-zinc-100">
                          {decision.decision === 'exit_before'
                            ? 'VORAB-EXIT empfohlen'
                            : decision.decision === 'reduce_size'
                              ? 'POSITION REDUZIEREN'
                              : 'HALTEN'}
                        </span>
                        <span className="text-zinc-400 ml-1">{decision.confidence}% Confidence</span>
                      </div>
                      <p className="text-zinc-200 leading-snug mb-1.5">{decision.headline}</p>
                      <p className="text-zinc-400 text-[10px] leading-snug">
                        {decision.reasoning}
                      </p>
                      {decision.pros_holding && decision.pros_holding.length > 0 && (
                        <div className="mt-1.5">
                          <span className="text-[9px] uppercase text-emerald-400 font-mono">Pro Halten:</span>
                          <ul className="ml-3 list-disc text-[10px] text-zinc-300">
                            {decision.pros_holding.map((p, i) => <li key={i}>{p}</li>)}
                          </ul>
                        </div>
                      )}
                      {decision.cons_holding && decision.cons_holding.length > 0 && (
                        <div className="mt-1">
                          <span className="text-[9px] uppercase text-red-400 font-mono">Contra Halten:</span>
                          <ul className="ml-3 list-disc text-[10px] text-zinc-300">
                            {decision.cons_holding.map((p, i) => <li key={i}>{p}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })()
              )}

              {/* Datapoints */}
              <div className="grid grid-cols-4 gap-1 text-[10px] font-mono pt-1.5 border-t border-zinc-800/40">
                <Datapoint
                  label="R-Multi"
                  value={
                    r.current_r_multiple !== null
                      ? `${r.current_r_multiple >= 0 ? '+' : ''}${r.current_r_multiple.toFixed(1)}R`
                      : '–'
                  }
                  tone={
                    r.current_r_multiple === null
                      ? 'neutral'
                      : r.current_r_multiple >= 1.5
                        ? 'good'
                        : r.current_r_multiple >= 0
                          ? 'neutral'
                          : 'bad'
                  }
                />
                <Datapoint
                  label="vs Entry"
                  value={
                    r.pct_above_entry !== null
                      ? `${r.pct_above_entry >= 0 ? '+' : ''}${r.pct_above_entry.toFixed(1)}%`
                      : '–'
                  }
                  tone={
                    r.pct_above_entry === null
                      ? 'neutral'
                      : r.pct_above_entry >= 0
                        ? 'good'
                        : 'bad'
                  }
                />
                <Datapoint
                  label="Score"
                  value={
                    r.v51_score_now !== null
                      ? `${r.v51_score_now}${
                          r.v51_score_delta_7d !== null
                            ? ` (${r.v51_score_delta_7d >= 0 ? '+' : ''}${r.v51_score_delta_7d.toFixed(0)})`
                            : ''
                        }`
                      : '–'
                  }
                  tone={
                    r.v51_score_delta_7d === null
                      ? 'neutral'
                      : r.v51_score_delta_7d >= 5
                        ? 'good'
                        : r.v51_score_delta_7d <= -5
                          ? 'bad'
                          : 'neutral'
                  }
                />
                <Datapoint
                  label={r.max_hold_days ? 'Zeit-Stopp' : 'Tage'}
                  value={
                    r.days_held !== null
                      ? r.max_hold_days
                        ? `${r.days_held}/${r.max_hold_days}d`
                        : `${r.days_held}d`
                      : '–'
                  }
                  tone={
                    r.days_held !== null && r.max_hold_days
                      ? r.days_held >= r.max_hold_days
                        ? 'bad'
                        : r.days_held / r.max_hold_days >= 0.8
                          ? 'warn'
                          : 'neutral'
                      : 'neutral'
                  }
                />
              </div>
            </li>
          );
        })}
      </ul>

      <p className="text-[10px] text-amber-200/80 leading-snug pt-2 border-t border-amber-500/20">
        <strong>Wichtig zur Stop-Empfehlung:</strong> Sie stammt aus gaengigen
        Lehrbuchregeln (Breakeven bei 1R, MA50-Trail ab 2R, Teilgewinn ab 3R) —
        <em>nicht</em> aus einer Messung an diesen Daten. Die Ruecktests vom
        08.08.2026 haben genau solche Nachzieh-Ausstiege geprueft und sie waren
        durchgehend <em>schlechter</em> als ein fester Stop: Chandelier 3×ATR
        stieg im Schnitt nach 28 Tagen aus, MA50 nach 21 — bei Phasen, die im
        Median 48 Handelstage laufen. Belastbar ist allein der gestaffelte
        Ausstieg unter „Was ist zu tun" (Haelfte bei +2R, dann Stop auf
        Einstand). Bei Widerspruch gilt der, nicht diese Karte.
      </p>
      <p className="text-[10px] text-zinc-600 leading-snug">
        <strong>Health-Score-Faktoren:</strong> R-Multiple aktuell · V5.1-Score-Trend
        7T · MA50/200-Position · Earnings-Naehe · Conviction-Tier. Der Score selbst
        ist eine Punktsumme ohne Rueckrechnung — seine Eingangsdaten liegen
        historisch nicht vor, ein Backtest waere zwangslaeufig Look-ahead.
      </p>
    </div>
  );
}

function Datapoint({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'good' | 'neutral' | 'warn' | 'bad';
}) {
  return (
    <div className="rounded border border-zinc-800/40 bg-zinc-950/30 px-1.5 py-1">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div
        className={cn(
          'text-[11px] mt-0.5',
          tone === 'good'
            ? 'text-emerald-300'
            : tone === 'bad'
              ? 'text-red-300'
              : tone === 'warn'
                ? 'text-amber-300'
                : 'text-zinc-200'
        )}
      >
        {value}
      </div>
    </div>
  );
}

// Avoid unused import warning
const _icons = { TrendingUp };
void _icons;
